import React from 'react';
import { createPortal } from 'react-dom';
import { useWeb3Auth } from '../../providers/Web3AuthProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { X, Copy } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';

interface ReceiveTokensModalProps {
	isOpen: boolean;
	onClose: () => void;
}

export function ReceiveTokensModal({
	isOpen,
	onClose,
}: ReceiveTokensModalProps) {
	const { walletAddress } = useWeb3Auth();

	if (!isOpen) return null;

	// Generate QR code URL using a free QR code API
	const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${walletAddress}`;

	const copyToClipboard = () => {
		if (walletAddress) {
			navigator.clipboard.writeText(walletAddress);
			toast({
				title: 'Address Copied',
				description: 'Wallet address copied to clipboard',
			});
		}
	};

	const modalContent = (
		<div className='fixed inset-0 z-[9999] overflow-hidden'>
			{/* Full-screen backdrop with pink/purple gradient blur */}
			<div
				className='fixed inset-0 bg-black/80 backdrop-blur-sm'
				onClick={onClose}
				style={{
					position: 'fixed',
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
				}}
			/>

			{/* Modal content */}
			<div className='fixed left-[50%] top-[50%] z-[10000] w-full max-w-md translate-x-[-50%] translate-y-[-50%] duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]'>
				<div className='shadow-[0_0_20px_2px_rgba(236,72,153,0.3)] border border-pink-900/50 bg-gradient-to-b from-zinc-900 to-black overflow-hidden rounded-xl'>
					{/* Header */}
					<div className='flex items-center justify-between p-5 '>
						<h2 className='text-xl font-bold text-white'>
							Receive{' '}
							<span className='text-pink-500'>$KARMA</span>
						</h2>
						<Button
							variant='ghost'
							size='sm'
							onClick={onClose}
							className='text-pink-300 hover:text-white hover:bg-pink-800/30 transition-all duration-200 h-8 w-8 p-0'
						>
							<X className='h-5 w-5' />
						</Button>
					</div>

					{/* Body */}
					<div className='p-6 space-y-5'>
						<div className='flex flex-col items-center'>
							{walletAddress && (
								<div className='bg-white p-3 rounded-lg mb-5 shadow-[0_0_15px_rgba(236,72,153,0.5)]'>
									<img
										src={qrCodeUrl}
										alt='Wallet Address QR Code'
										className='w-52 h-52'
									/>
								</div>
							)}

							<div className='w-full mt-3 space-y-2'>
								<Label className='text-sm font-medium text-pink-200'>
									Your Wallet Address
								</Label>
								<div className='relative'>
									<Input
										type='text'
										value={walletAddress || ''}
										readOnly
										className='bg-zinc-800/70 border-pink-900/30 hover:border-pink-500/50 focus:border-pink-500 text-white font-mono text-sm pr-16'
									/>
									<Button
										onClick={copyToClipboard}
										variant='ghost'
										size='sm'
										className='absolute right-1 top-1/2 transform -translate-y-1/2 h-7 text-pink-300 hover:text-white hover:bg-pink-800/30'
									>
										<Copy className='h-4 w-4 mr-1' />
									</Button>
								</div>
							</div>

							<p className='text-sm text-pink-300/70 text-center mt-4'>
								Share this address with others to receive KARMA
								tokens
							</p>
						</div>

						<div className='flex justify-end pt-3'>
							<Button
								type='button'
								onClick={onClose}
								className='bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white shadow-[0_0_10px_rgba(168,85,247,0.3)]'
							>
								Close
							</Button>
						</div>
					</div>
				</div>
			</div>
		</div>
	);

	// Use createPortal to render the modal directly in the document body
	return createPortal(modalContent, document.body);
}
